import { useCallback, useState } from "react";
import { api } from "../utils/api";

export type DateStateLabel =
  | "couple" | "none" | "liked" | "liked_by_them" | "matched"
  | "invitation_sent" | "invitation_received" | "confirmed";

export interface DateInvitation {
  id: string;
  match_id: string;
  inviter_id: string;
  invitee_id: string;
  activity_key: string;
  venue_id: string | null;
  place_name: string;
  place_address: string | null;
  place_lat: number | null;
  place_lng: number | null;
  scheduled_date: string;
  scheduled_time: string;
  price_note: string | null;
  status: string;
  expires_at: string;
  conversation_id: string | null;
}

export interface PairDateState {
  is_couple: boolean;
  liked_by_me: boolean;
  likes_me: boolean;
  match_id: string | null;
  invitation: DateInvitation | null;
  conversation_id: string | null;
  state: DateStateLabel;
}

export interface SendInvitationPayload {
  activity_key: string;
  place: { name: string; address?: string; lat?: number; lng?: number; venue_id?: string };
  date: string;
  time: string;
  price_note?: string;
}

const EMPTY_STATE: PairDateState = {
  is_couple: false, liked_by_me: false, likes_me: false, match_id: null,
  invitation: null, conversation_id: null, state: "none",
};

// État Like → Match → Date pour un profil visité, et les actions qui le font évoluer. La seule
// source de vérité pour l'état est le serveur (GET /dates/state/:userId, voir
// utils/dateState.ts côté backend) — ce hook ne fait jamais lui-même la moindre déduction
// d'état, il se contente d'afficher/rafraîchir ce que l'API renvoie. `hydrate()` permet à la
// page profil d'amorcer l'affichage avec les champs déjà présents dans la réponse de
// GET /users/:id (liked_by_me, is_couple, match_id, date_state...), sans requête supplémentaire.
export function useDateState(userId: string) {
  const [state, setState] = useState<PairDateState>(EMPTY_STATE);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const hydrate = useCallback((data: any) => {
    if (!data) return;
    setState({
      is_couple: !!data.is_couple,
      liked_by_me: !!data.liked_by_me,
      likes_me: !!data.likes_me,
      match_id: data.match_id ?? null,
      invitation: data.date_invitation ?? null,
      conversation_id: data.date_conversation_id ?? null,
      state: data.date_state ?? "none",
    });
  }, []);

  const refresh = useCallback(async () => {
    const data = await api.get(`/dates/state/${userId}`);
    if (data?.state) setState(data);
    return data;
  }, [userId]);

  const like = useCallback(async () => {
    setError(null);
    const data = await api.post(`/users/${userId}/like`, {});
    if (data?.code === "IN_RELATIONSHIP") {
      setError("IN_RELATIONSHIP");
      await refresh();
      return data;
    }
    if (data?.state) {
      setState(prev => ({ ...prev, liked_by_me: true, match_id: data.match_id, state: data.state }));
    }
    return data;
  }, [userId, refresh]);

  const sendInvitation = useCallback(async (payload: SendInvitationPayload) => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.post("/dates/invitations", { user_id: userId, ...payload });
      if (data?.code) { setError(data.code); return data; }
      if (data?.invitation) setState(prev => ({ ...prev, invitation: data.invitation, state: "invitation_sent" }));
      return data;
    } finally {
      setLoading(false);
    }
  }, [userId]);

  const accept = useCallback(async (invitationId: string) => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.post(`/dates/invitations/${invitationId}/accept`, {});
      if (data?.code) { setError(data.code); return data; }
      if (data?.state) {
        setState(prev => ({ ...prev, invitation: data.invitation, conversation_id: data.conversation_id, state: data.state }));
      }
      return data;
    } finally {
      setLoading(false);
    }
  }, []);

  const decline = useCallback(async (invitationId: string) => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.post(`/dates/invitations/${invitationId}/decline`, {});
      if (data?.code) { setError(data.code); return data; }
      await refresh();
      return data;
    } finally {
      setLoading(false);
    }
  }, [refresh]);

  return { ...state, loading, error, hydrate, refresh, like, sendInvitation, accept, decline };
}
